import { Variant } from 'shared/types/product';
import { NON_AXIS_KEYS } from './optionAxes';

/**
 * The cache key for a variant's base gallery image.
 *
 * Cloudinary tags base assets `product_id:<sku>`, so the image is per-SKU — but a SKU is defined by
 * more than the photograph shows, and refetching for a difference the camera cannot see is wasted on
 * a high-variant product. So the key is the variant's configurable attributes, minus the
 * `IMAGE_IRRELEVANT_AXES` exceptions below.
 *
 * What actually reaches this helper is already narrow: `ProductApi.enrichProduct` replaces
 * `variant.attributes` on the PDP payload, dropping `finishes`, `sheens`, `top-style`, `infill`,
 * `mounting-style` and every dimension/pricing/`variant-name` key. So on `/p/<sku>` the input is
 * essentially the configurator's own axes — `material`, `handrail-height`, `turn-direction`,
 * `post-location`, `wood-type`, … — and the `NON_AXIS_KEYS` filter below is redundant belt-and-braces
 * rather than load-bearing. It is kept because this helper must not silently start keying on a spec
 * row if that backend list ever changes, and because nothing links the two lists.
 *
 * That coupling is the standing risk here: an attribute added to the enriched payload that is unique
 * per SKU, and absent from both exclusion lists, would re-fan the cache to one entry per variant
 * (252 on the Angle Tension Post). That is a request-limiter burst, not a wrong image.
 *
 * Reading only the current variant is deliberate. An earlier version derived the key set by scanning
 * every variant for attributes that *vary*, which made it depend on `product.variants` being the
 * complete list — if that were ever filtered to the combinations reachable from the current
 * selection, an axis would collapse to a single value, silently drop out of the key, and reintroduce
 * the stale-image bug this file exists to fix. A per-variant key cannot fail that way.
 */

/**
 * Attributes that define a SKU but never change the photograph, so they must not enter the key.
 *
 * `system-angle` is the reason this list exists: the Angle Tension Post offers 14 angles of the same
 * photographed post, so keying on it would mean 252 cache keys for 18 real images — 13 of every 14
 * configurations a shopper lands on paying a Cloudinary round-trip to be handed back the image it
 * already had.
 *
 * A missing entry here can only cost a redundant fetch that returns the *correct* image; it can
 * never show the wrong one. Erring toward leaving an attribute out is therefore always safe, which
 * is why the default is to include everything and subtract only known exceptions.
 */
const IMAGE_IRRELEVANT_AXES = ['system-angle'];

/**
 * One attribute value as a stable string, or `undefined` when it holds nothing usable.
 *
 * The backend mapper resolves localized text to a plain string but leaves enum values as
 * `{ key, label }` and numbers as numbers, so all three shapes reach the frontend.
 */
export const getAttributeValue = (variant: Variant, key: string): string | undefined => {
  const raw = variant.attributes?.[key];

  if (typeof raw === 'string') return raw || undefined;
  if (typeof raw === 'number') return String(raw);
  if (raw && typeof raw === 'object' && 'key' in raw) {
    const { key: enumKey } = raw as { key: unknown };
    return typeof enumKey === 'string' || typeof enumKey === 'number' ? String(enumKey) : undefined;
  }
  return undefined;
};

/**
 * `alsoIgnore` lets a caller ask "which configuration is this, apart from these attributes" — the
 * prefetch hook uses it to find the variant a shopper would land on when they change only the lead
 * axis. Keys are sorted and name-tagged so the string is stable regardless of attribute order in the
 * payload, and so a value moving between attributes cannot produce the same key.
 */
export const getImageCacheKey = (variant: Variant, alsoIgnore: string[] = []): string => {
  const segments = Object.keys(variant.attributes ?? {})
    .filter((key) => !NON_AXIS_KEYS.includes(key) && !IMAGE_IRRELEVANT_AXES.includes(key) && !alsoIgnore.includes(key))
    .sort()
    .map((key) => `${key}:${getAttributeValue(variant, key) ?? ''}`);

  // A variant with no configurable attributes at all still needs a distinct key; the SKU is what
  // Cloudinary's tagging implies anyway.
  return segments.length ? segments.join('|') : variant.sku;
};
